import { useEffect, useState } from "react";
import api from "../../utils/api";
import ActivePlanTab from "./ActivePlanTab";
import PlanHistoryTab from "./PlanHistoryTab";
import PaymentHistoryTab from "./PaymentHistoryTab";

const TABS = ["active", "history", "payments"];

const SubscriptionPage = () => {
  const [activeTab, setActiveTab] = useState("active");
  const [subscription, setSubscription] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    totalPages: 1,
    limit: 10,
  });
  const [payments, setPayments] = useState([]);
  const [paymentPagination, setPaymentPagination] = useState({
    page: 1,
    totalPages: 1,
  });
  const [historySortOrder, setHistorySortOrder] = useState("desc");
  const [paymentSortOrder, setPaymentSortOrder] = useState("desc");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch plan history with pagination and sorting
  const fetchPlanHistory = async (page = 1, sort = historySortOrder) => {
    try {
      setHistoryLoading(true);
      const res = await api.get(
        `subscriptions/history?page=${page}&limit=10&sort=${sort}`,
      );
      setHistory(res.data.data || []);
      setHistoryPagination(res.data.pagination || { 
        page: 1, 
        totalPages: 1, 
        totalItems: 0,
        limit: 10
      });
      setError(null);
    } catch (err) {
      console.error("Error fetching plan history:", err);
      setError("Failed to load plan history");
      setHistory([]);
      setHistoryPagination({ page: 1, totalPages: 1, totalItems: 0, limit: 10 });
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetch payments with pagination and sorting
  const fetchPayments = async (page = 1, sort = paymentSortOrder) => {
    try {
      setPaymentLoading(true);
      const res = await api.get(
        `payments/my?page=${page}&limit=5&sort=${sort}`,
      );
      setPayments(res.data.data || []);
      setPaymentPagination(res.data.pagination || { page: 1, totalPages: 1 });
      setError(null);
    } catch (err) {
      console.error("Error fetching payments:", err);
      setError("Failed to load payment history");
      setPayments([]);
      setPaymentPagination({ page: 1, totalPages: 1 });
    } finally {
      setPaymentLoading(false);
    }
  };

  // Fetch active subscription
  const fetchActiveSubscription = async () => {
    try {
      setLoading(true);
      const subRes = await api.get("subscriptions/current");
      setSubscription(subRes.data.data);
    } catch (err) {
      console.error("Error fetching active subscription:", err);
      setError("Failed to load active subscription");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        fetchActiveSubscription(),
        fetchPlanHistory(1, historySortOrder),
        fetchPayments(1, paymentSortOrder)
      ]);
    };

    fetchData();
  }, []);

  // Plan history handlers
  const handleHistorySortChange = (newSort) => {
    setHistorySortOrder(newSort);
    fetchPlanHistory(1, newSort);
  };

  const handleHistoryPageChange = (page) => {
    fetchPlanHistory(page, historySortOrder);
  };

  // Payment history handlers
  const handlePaymentSortChange = (newSort) => {
    setPaymentSortOrder(newSort);
    fetchPayments(1, newSort);
  };

  const handlePaymentPageChange = (page) => {
    fetchPayments(page, paymentSortOrder);
  };

  // Get loading state for active tab
  const getTabLoadingState = () => {
    if (activeTab === "active") return loading;
    if (activeTab === "history") return historyLoading;
    if (activeTab === "payments") return paymentLoading;
    return false;
  };

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold text-teal-600 mb-6 text-center">
          Subscription Management
        </h1>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
            <p className="font-medium">Error: {error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-2 text-sm underline hover:text-red-800"
            >
              Try again
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 bg-white p-2 rounded-xl shadow-sm">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 md:px-5 py-2 rounded-lg font-medium transition-all duration-200 ${
                activeTab === tab
                  ? "bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-md"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab === "active" && "Active Plan"}
              {tab === "history" && "Plan History"}
              {tab === "payments" && "Payment History"}
            </button>
          ))}
        </div>

        {/* Loading State for active tab */}
        {getTabLoadingState() && activeTab !== "active" && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-600"></div>
          </div>
        )}

        {/* Content */}
        {activeTab === "active" && !loading && (
          <ActivePlanTab subscription={subscription} />
        )}

        {activeTab === "history" && !historyLoading && (
          <PlanHistoryTab
            history={history}
            pagination={historyPagination}
            sortOrder={historySortOrder}
            onSortChange={handleHistorySortChange}
            onPageChange={handleHistoryPageChange}
            loading={historyLoading}
          />
        )}

        {activeTab === "payments" && !paymentLoading && (
          <PaymentHistoryTab
            payments={payments}
            pagination={paymentPagination}
            sortOrder={paymentSortOrder}
            onSortChange={handlePaymentSortChange}
            onPageChange={handlePaymentPageChange}
            loading={paymentLoading}
          />
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;